import React, { memo, useCallback } from 'react';
import flow from 'lodash/flow';
import cn from 'classnames';
import { toCommaSeparated } from '@utils/formatting';
import Star from './Star';

/* styles */
import styles from './starrating.module.scss';

/* Types */
type StarRatingProps = {
  size?: number;
  stars: number;
  count?: number;
  fullStars?: number;
  partialStarValue?: number;
  emptyStars?: number;
  onClick?: BaseAnyFunction;
  tag: React.FC<any>;
  className?: string;
};

/* constants */
const SIZES = {
  sm: 14,
  md: 20,
  lg: 25,
};
const MAX = 5;
const enhance = flow(
  memo,

  Component =>
    ({ size, stars, onClick, count, ...rest }: StarRatingProps) => {
      const fullStars = Math.floor(stars);
      const partialStarValue = stars % 1;
      const emptyStars = MAX - (fullStars + (partialStarValue !== 0 ? 1 : 0));
      const props = {
        fullStars,
        partialStarValue,
        emptyStars,
        size: size ? (SIZES as Record<string, number>)[size] : SIZES.md,
        onClick,
        count: count && toCommaSeparated(count),
        ...rest,
        tag: onClick ? 'a' : 'span',
      };
      return <Component {...props} />;
    },
);

const StarRating = ({ size = 25, fullStars, partialStarValue, emptyStars, count = 0, onClick, tag: Tag, className }: StarRatingProps) => {
  const handleClick = useCallback(
    (event: Event) => {
      event.preventDefault();
      onClick?.();
    },
    [onClick],
  );

  const numberCount = typeof count === 'string' ? parseInt((count as string).replace(/,/g, ''), 10) : count; // check if count is a string

  return (
    <Tag href="#" className={cn(styles.rating, className)} onClick={numberCount > 2 ? handleClick : null} data-testid="star-ratings">
      {[...Array(fullStars)].map((_, idx) => (
        <Star value={1} size={size} key={`fs${idx}`} className={styles.star} />
      ))}
      {partialStarValue !== 0 && <Star value={partialStarValue!} size={size} className={styles.star} />}
      {[...Array(emptyStars)].map((_, idx) => (
        <Star value={0} size={size} key={`es${idx}`} className={styles.star} />
      ))}
      {!!count && <span className={cn('caption n700', styles.count)}>{count}</span>}
    </Tag>
  );
};

export default enhance(StarRating);
